import React, { useState, useEffect, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Title from '../components/Title';
import SubmitButton from '../components/SubmitButton';
import NavigationStatus from '../components/NavigationStatus';
import useGtmEventTracking from '../components/useGtmEventTracking';
import { displayLoginName } from '../components/user/DisplayLoginName';
import UserContext from '../contexts/UserContext';
import { axiosKK } from '../services/networkRequest';
import errorMessage from '../services/errorMessage';
import { useFlow } from '../utilities/useFlow';

interface RemovalTier {
  id: string;
  name: string;
  price: number | null;
  description: string;
  imageUrl: string;
  details: string;
  requiresQuote?: boolean;
  mostPopular?: boolean;
}

const removalTiers: RemovalTier[] = [
  {
    id: 'mini',
    name: 'Mini',
    price: 19900, // £199 in pence
    description: 'Up to 10 boxes or items, items that can be moved by one person.',
    imageUrl: '/img/MiniRemoval.jpg',
    details: 'Up to 25 miles & 2 hours included'
  },
  {
    id: 'small',
    name: 'Small',
    price: 34900, // £349 in pence
    description: 'Up to 20 items including boxes and small furniture',
    imageUrl: '/img/SmallRemoval.png',
    details: 'Up to 25 miles & 3 hours included'
  },
  {
    id: 'standard',
    name: 'Standard',
    price: 49900, // £499 in pence
    description: 'Up to 30 items including boxes and furniture, equivalent to 400ft3 with 2 movers.',
    imageUrl: '/img/StandardRemoval.png',
    details: 'Up to 25 miles & 5 hours included (if packing required)',
    mostPopular: true
  },
  {
    id: 'large',
    name: 'Large',
    price: 69900, // £699 in pence
    description: 'Up to a full large Luton van (600ft3), with 2 movers',
    imageUrl: '/img/LargeRemoval.png',
    details: 'Up to 25 miles & 7 hours included (if packing required)'
  },
  {
    id: '3bed',
    name: '3+ bed property',
    price: null,
    description: 'Full house removal service',
    imageUrl: '/img/3BedRemoval.jpg',
    details: 'Requires a custom quote',
    requiresQuote: true
  },
  {
    id: 'business',
    name: 'Business',
    price: null,
    description: 'Office and commercial relocations',
    imageUrl: '/img/BusinessRemoval.png',
    details: 'Requires a custom quote',
    requiresQuote: true
  }
];

interface QuoteRequestPayload {
  tierType: string;
  tierName: string;
  customerEmail: string;
  customerPhone: string;
  message: string;
  customerId: number | null;
}

interface QuoteRequestResponse {
  message: string;
}

interface FormErrors {
  email?: string;
  phone?: string;
  message?: string;
}

function RemovalsComponent(): JSX.Element {
  const [selectedTier, setSelectedTier] = useState<RemovalTier>(removalTiers[2]); // Default to 'Standard' (most popular)
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [quoteEmail, setQuoteEmail] = useState('');
  const [quotePhone, setQuotePhone] = useState('');
  const [quoteMessage, setQuoteMessage] = useState('');
  const [quoteSubmitted, setQuoteSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [networkError, setNetworkError] = useState<string | undefined>();
  const [successMessage, setSuccessMessage] = useState('');
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const history = useHistory();
  const gtmEventTracking = useGtmEventTracking();
  const { user } = useContext(UserContext);
  const flow = useFlow();

  useEffect(() => {
    window.scrollTo(0, 0);
    gtmEventTracking({ event: 'booking_step_removals' });
  }, [gtmEventTracking]);

  // Pre-fill email when modal opens if user is logged in
  useEffect(() => {
    if (showQuoteModal && user && user.email) {
      setQuoteEmail(user.email);
    }
  }, [showQuoteModal, user]);

  const handleContinue = () => {
    if (selectedTier.requiresQuote) {
      setShowQuoteModal(true);
    } else {
      // Navigate to questions page with selected tier
      history.push('/removals/questions', {
        selectedTier,
        boxes: [], // Removals don't use boxes
        removalService: true
      });
    }
  };

  const validateForm = (): boolean => {
    const errors: FormErrors = {};
    
    // Email validation
    if (!quoteEmail || !/\S+@\S+\.\S+/.test(quoteEmail)) {
      errors.email = 'Please enter a valid email address';
    }
    
    // Phone validation
    if (!quotePhone || quotePhone.trim().length < 10) {
      errors.phone = 'Please enter a valid phone number';
    }
    
    // Message validation (minimum 10 characters)
    if (!quoteMessage || quoteMessage.trim().length < 10) {
      errors.message = 'Please provide more details (at least 10 characters)';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleQuoteSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Clear previous errors
    setNetworkError(undefined);
    setFormErrors({});
    
    // Validate form
    if (!validateForm()) {
      return;
    }
    
    setSubmitting(true);
    
    try {
      const requestData: QuoteRequestPayload = {
        tierType: selectedTier.id, // "3bed" or "business"
        tierName: selectedTier.name, // Display name
        customerEmail: quoteEmail,
        customerPhone: quotePhone,
        message: quoteMessage,
        customerId: user?.id || null
      };
      
      const response = await axiosKK.post<QuoteRequestResponse>(
        '/removals/quote-request', 
        requestData
      );
      
      // Show success message
      setQuoteSubmitted(true);
      setSuccessMessage(response.data.message || "Quote request submitted successfully. We'll get back to you within 24 hours.");
      
      // GTM tracking
      gtmEventTracking({ 
        event: 'quote_request_submitted', 
        tier: selectedTier.name 
      });
      
    } catch (error) {
      errorMessage(setNetworkError)(error as Error);
    } finally {
      setSubmitting(false);
    }
  };

  const closeQuoteModal = (navigateToHome = false) => {
    setShowQuoteModal(false);
    setQuoteEmail('');
    setQuotePhone('');
    setQuoteMessage('');
    setQuoteSubmitted(false);
    setSubmitting(false);
    setNetworkError(undefined);
    setSuccessMessage('');
    setFormErrors({});
    
    if (navigateToHome) {
      history.push('/');
    }
  };

  return (
    <>
      <Helmet>
        <title>{`Removals Service - Book Your Move | ${flow}`}</title>
        <meta
          name="description"
          content={`Professional removals service across the UK from ${flow}. From mini moves to full house relocations. Get an instant price or request a custom quote.`}
        />
      </Helmet>
      <section className="login-status-bar">
        <div className="status-bar-content">
          <p className="grey">{displayLoginName(user)}</p>
        </div>
      </section>
      <NavigationStatus step={1} />

      <div className="funnel-layout mt-5">
        <div className="title mb-3" style={{ textAlign: 'center' }}>
          <Title>How big is your move?</Title>
        </div>
        
        <div className="content mt-5">
          <div className="form">
            <div className="sections questions" style={{ textAlign: 'center' }}>
              
              <div className="available-tiers">
                {/* Current Selected Tier Display */}
                <div className="tier-wrapper">
                  <div className="current-tier">
                    <div className="removals-card">
                      {selectedTier.mostPopular && (
                        <div className="most-popular-badge">Most Popular</div>
                      )}
                      <div className="removals-inner">
                        <div className="tier-image">
                          <img 
                            className="b-img img-fluid" 
                            src={selectedTier.imageUrl} 
                            loading="eager" 
                            alt={selectedTier.name}
                          />
                        </div>
                        <div className="tier-details">
                          <h3 className="ls-bold ls-purple">
                            {selectedTier.price ? `£${(selectedTier.price / 100).toFixed(0)}` : 'Custom Quote'}
                          </h3>
                          <div>{selectedTier.description}</div>
                          <div style={{ marginTop: '8px', fontSize: '14px', color: '#666' }}>
                            {selectedTier.details}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Tier Selection Options */}
                <div className="options">
                  {removalTiers.map((tier) => (
                    <div 
                      key={tier.id}
                      className={`removals-card ${selectedTier.id === tier.id ? 'selected' : ''}`}
                      onClick={() => setSelectedTier(tier)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="compact ls-bold">{tier.name}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="tier-footer">
                <SubmitButton onClick={handleContinue}>
                  Continue
                </SubmitButton>
              </div>
            </div>

            <div className="questions" style={{ textAlign: 'center' }}>
              <div className="question">
                Need by-the-box storage? Check out our{' '}
                <a href="/storage/boxes" className="">booking portal</a>
              </div>
            </div>
          </div>

          <div id="funnelFooter" className="mt-3"></div>
        </div>
      </div>

      {/* Custom Quote Modal */}
      {showQuoteModal && (
        <div className="modal-overlay" onClick={() => closeQuoteModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Request Custom Quote - {selectedTier.name}</h3>
            </div>
            {!quoteSubmitted ? (
              <form onSubmit={handleQuoteSubmit}>
                <div className="modal-body">
                  <div style={{ 
                    backgroundColor: '#f5f3ff', 
                    borderRadius: '12px', 
                    padding: '20px',
                    marginBottom: '25px',
                    border: '2px solid var(--kkUltraVioletVeryLight)'
                  }}>
                    <div style={{ 
                      fontSize: '16px', 
                      fontWeight: 'bold', 
                      color: 'var(--kkUltraViolet)', 
                      marginBottom: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px'
                    }}>
                      <span style={{ fontSize: '24px' }}>ℹ️</span>
                      What We Need From You
                    </div>
                    <p style={{ marginBottom: '10px', color: '#555', fontSize: '14px' }}>
                      We'll create a tailored quote specifically for your {selectedTier.name.toLowerCase()} needs. 
                      Please provide your contact details and tell us more about your requirements.
                    </p>
                  </div>
                  
                  {networkError && (
                    <div className="error-message" style={{ 
                      color: 'var(--CherryRed)', 
                      padding: '10px', 
                      marginBottom: '15px',
                      backgroundColor: 'var(--LightRed)',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}>
                      {networkError}
                    </div>
                  )}

                  <div className="form-group">
                    <label htmlFor="quoteEmail">Your Email Address *</label>
                    <input
                      type="email"
                      id="quoteEmail"
                      value={quoteEmail}
                      onChange={(e) => {
                        setQuoteEmail(e.target.value);
                        if (formErrors.email) setFormErrors({ ...formErrors, email: undefined });
                      }}
                      disabled={submitting}
                      placeholder="your.email@example.com"
                      className={formErrors.email ? 'error' : ''}
                    />
                    {formErrors.email && (
                      <span className="field-error" style={{ color: 'var(--CherryRed)', fontSize: '13px', marginTop: '8px', display: 'block', textAlign: 'center' }}>
                        {formErrors.email}
                      </span>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="quotePhone">Your Phone Number *</label>
                    <input
                      type="tel"
                      id="quotePhone"
                      value={quotePhone}
                      onChange={(e) => {
                        setQuotePhone(e.target.value);
                        if (formErrors.phone) setFormErrors({ ...formErrors, phone: undefined });
                      }}
                      disabled={submitting}
                      placeholder="+44 7XXX XXXXXX"
                      className={formErrors.phone ? 'error' : ''}
                    />
                    {formErrors.phone && (
                      <span className="field-error" style={{ color: 'var(--CherryRed)', fontSize: '13px', marginTop: '8px', display: 'block', textAlign: 'center' }}>
                        {formErrors.phone}
                      </span>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="quoteMessage">
                      Tell us about your removal needs *
                    </label>
                    <div style={{ 
                      backgroundColor: '#f9f9f9', 
                      padding: '15px', 
                      borderRadius: '8px', 
                      marginBottom: '12px',
                      textAlign: 'left',
                      border: '1px solid #e0e0e0'
                    }}>
                      <div style={{ fontSize: '13px', color: '#555', marginBottom: '8px', fontWeight: '600' }}>
                        Please include:
                      </div>
                      <ul style={{ 
                        margin: '0', 
                        paddingLeft: '20px', 
                        fontSize: '13px', 
                        color: '#666',
                        lineHeight: '1.6'
                      }}>
                        <li>Collection and delivery addresses</li>
                        <li>Property size and type (e.g., 3-bed house, 2-bed flat)</li>
                        <li>Approximate number of items and furniture</li>
                        <li>Any special requirements (fragile items, heavy furniture, etc.)</li>
                        <li>Preferred moving date and time</li>
                      </ul>
                    </div>
                    <div style={{ textAlign: 'center', fontSize: '12px', color: 'var(--grey)', marginBottom: '8px' }}>
                      {quoteMessage.length} characters
                    </div>
                    <textarea
                      id="quoteMessage"
                      value={quoteMessage}
                      onChange={(e) => {
                        setQuoteMessage(e.target.value);
                        if (formErrors.message) setFormErrors({ ...formErrors, message: undefined });
                      }}
                      disabled={submitting}
                      rows={9}
                      placeholder="Example: Moving from a 3-bed house in Manchester to a 2-bed flat in London. Approximately 50 items including furniture, kitchen appliances, and boxes. Need help with a piano. Preferred move date: 15th November..."
                      className={formErrors.message ? 'error' : ''}
                      style={{ resize: 'none', height: '180px', width: '100%' }}
                    />
                    {formErrors.message && (
                      <span className="field-error" style={{ color: 'var(--CherryRed)', fontSize: '13px', marginTop: '8px', display: 'block', textAlign: 'center' }}>
                        {formErrors.message}
                      </span>
                    )}
                    <span style={{ fontSize: '12px', color: 'var(--grey)', marginTop: '8px', display: 'block', textAlign: 'center' }}>
                      Minimum 10 characters required
                    </span>
                  </div>
                </div>
                <div className="modal-footer">
                  <button 
                    type="button" 
                    className="btn-secondary" 
                    onClick={() => closeQuoteModal(false)}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <SubmitButton type="submit" disabled={submitting}>
                    {submitting ? 'Sending...' : 'Request Quote'}
                  </SubmitButton>
                </div>
              </form>
            ) : (
              <div className="modal-body">
                <div className="success-message">
                  <h4>✓ Quote request received!</h4>
                  <p>We've sent you a confirmation email at <strong>{quoteEmail}</strong>.</p>
                  <p>Our team will review your request and get back to you within 24 hours with a custom quote.</p>
                  {successMessage && (
                    <p style={{ fontSize: '14px', color: 'var(--grey)', marginTop: '10px' }}>
                      {successMessage}
                    </p>
                  )}
                  <SubmitButton onClick={() => closeQuoteModal(true)}>Close</SubmitButton>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default RemovalsComponent;
